import React, { useState, useEffect } from 'react';
import { Chart as ReactChart } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface ComparisonData {
  state: string;
  parameter: string;
  data: {
    labels: string[];
    values: number[];
  };
}

interface ComparisonPanelProps {
  locations: any[];
  activeCity?: string;
  selectedCities?: string[];
  onClose?: () => void;
  onFullscreenChange?: (fullscreen: boolean) => void;
  isFullscreen?: boolean;
}

const ComparisonPanel: React.FC<ComparisonPanelProps> = ({ 
  locations, 
  activeCity,
  selectedCities,
  onClose, 
  onFullscreenChange,
  isFullscreen = false 
}) => {
  const [comparisons, setComparisons] = useState<ComparisonData[]>([]);
  const [selectedState, setSelectedState] = useState('');
  const [selectedParameter, setSelectedParameter] = useState('');
  const [isResolutionSupported, setIsResolutionSupported] = useState(true);

  useEffect(() => {
    const checkResolution = () => {
      setIsResolutionSupported(window.innerHeight > 700);
    };

    checkResolution();
    window.addEventListener('resize', checkResolution);
    return () => window.removeEventListener('resize', checkResolution);
  }, []);

  // Do not persist comparisons; always reflect only current selection set
  useEffect(() => {
    // If selection cleared, clear comparisons as well
    if (!selectedCities || selectedCities.length === 0) {
      setComparisons([]);
      return;
    }
    // Prune any comparisons for cities no longer selected
    setComparisons(prev => prev.filter(c => selectedCities.includes(c.state)));
  }, [selectedCities]);

  // Only allow adding from explicitly selected cities
  const states = selectedCities && selectedCities.length > 0
    ? selectedCities
    : [];
  // Parameters depend on the chosen state (if any)
  const parameters = React.useMemo(() => {
    if (!selectedState) return [] as string[];
    const loc = locations.find(l => l.name === selectedState);
    return loc ? Object.keys(loc.parameters) : [];
  }, [locations, selectedState]);

  // Removed activeCity auto-add; comparisons derive only from selectedCities and manual adds

  // Helper to dedupe comparisons by (state, parameter)
  const dedupe = (arr: ComparisonData[]) => {
    const map = new Map<string, ComparisonData>();
    for (const c of arr) {
      map.set(`${c.state}|${c.parameter}`, c);
    }
    return Array.from(map.values());
  };

  // When selected cities change, ensure each appears once with a sensible default param
  useEffect(() => {
    if (!selectedCities || selectedCities.length === 0) return;
    const toAdd: ComparisonData[] = [];
    for (const city of selectedCities) {
      const loc = locations.find(l => l.name === city);
      if (!loc) continue;
      const hasSm = Array.isArray(loc.parameters?.SM?.series) && loc.parameters.SM.series.length > 0;
      const param = hasSm ? 'SM' : (Array.isArray(loc.parameters?.PRECTOT?.series) && loc.parameters.PRECTOT.series.length > 0 ? 'PRECTOT' : null);
      if (!param) continue;
      if (comparisons.some(c => c.state === city && c.parameter === param)) continue;
      const paramData = loc.parameters[param];
      toAdd.push({
        state: city,
        parameter: param,
        data: {
          labels: paramData.series.map((d: any) => `${d.year}-DOY${d.doy}`),
          values: paramData.series.map((d: any) => d.value)
        }
      });
    }
    if (toAdd.length > 0) setComparisons(prev => dedupe([...prev, ...toAdd]));
  }, [selectedCities, locations, comparisons]);

  const addComparison = () => {
    if (!selectedState || !selectedParameter) return;

    const location = locations.find(loc => loc.name === selectedState);
    if (!location) return;

    const paramData = location.parameters[selectedParameter];
    if (!paramData) return;

    const newComparison: ComparisonData = {
      state: selectedState,
      parameter: selectedParameter,
      data: {
        labels: paramData.series.map((d: any) => `${d.year}-DOY${d.doy}`),
        values: paramData.series.map((d: any) => d.value)
      }
    };

  // Avoid duplicates
  const exists = comparisons.some(c => c.state === newComparison.state && c.parameter === newComparison.parameter);
  if (exists) return;
  setComparisons(prev => dedupe([...prev, newComparison]));
    setSelectedState('');
    setSelectedParameter('');
  };

  const removeComparison = (index: number) => {
    const newComparisons = comparisons.filter((_, i) => i !== index);
    setComparisons(newComparisons);
  };

  const calculateStats = (values: number[]) => {
    return {
      average: values.reduce((a, b) => a + b, 0) / values.length,
      maximum: Math.max(...values),
      minimum: Math.min(...values)
    };
  };

  if (!isResolutionSupported) {
    return (
      <div className="comparison-panel">
        <div className="filter-sidebar-header">
          <h3>Compare</h3>
          <div className="header-buttons">
            {onClose && (
              <button className="filter-sidebar-close" onClick={onClose} title="Close">×</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`comparison-panel ${isFullscreen ? 'fullscreen' : ''}`}>
      <div className="filter-sidebar-header">
        <h3>Compare</h3>
        <div className="header-buttons">
          <button 
            className={`icon-button ${comparisons.length === 0 ? 'disabled' : ''}`}
            onClick={() => comparisons.length > 0 && onFullscreenChange?.(!isFullscreen)} 
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
            disabled={comparisons.length === 0}
          >
            {isFullscreen ? "⤓" : "⤢"}
          </button>
          {onClose && (
            <button className="filter-sidebar-close" onClick={onClose} title="Close">×</button>
          )}
        </div>
      </div>

      <div className="comparison-content">
        {states.length > 0 ? (
          <div className="comparison-controls">
            <div className="comparison-select">
              <select 
                value={selectedState} 
                onChange={(e) => setSelectedState(e.target.value)}
                className="comparison-dropdown"
              >
                <option value="">Select Location</option>
                {states.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>

              <select 
                value={selectedParameter} 
                onChange={(e) => setSelectedParameter(e.target.value)}
                className="comparison-dropdown"
                disabled={!selectedState}
              >
                <option value="">Select Parameter</option>
                {parameters.map(param => (
                  <option key={param} value={param}>{param}</option>
                ))}
              </select>

              <button 
                onClick={addComparison}
                className="add-comparison-btn"
                disabled={!selectedState || !selectedParameter}
              >
                +
              </button>
            </div>
          </div>
        ) : (
          <div className="no-comparisons-message">
            <p>No locations selected. Add from the map using “Add to Compare”.</p>
          </div>
        )}

  {comparisons.length > 0 ? (
          <>
            <div className="comparison-graph">
              {(() => {
                // Build union label set
                const labelSet = new Set<string>();
                for (const comp of comparisons) comp.data.labels.forEach(l => labelSet.add(l));
                const labels = Array.from(labelSet);
                labels.sort((a, b) => {
                  // labels like YYYY-DOYnnn
                  const [ay, ad] = a.replace('D', '').split('-DOY').map(Number);
                  const [by, bd] = b.replace('D', '').split('-DOY').map(Number);
                  return ay === by ? ad - bd : ay - by;
                });

                // Helper: map a comp's (labels, values) onto union
                const mapToUnion = (comp: typeof comparisons[number]) => {
                  const map = new Map<string, number>();
                  comp.data.labels.forEach((l, i) => map.set(l, comp.data.values[i]));
                  return labels.map(l => (map.has(l) ? map.get(l)! : null));
                };

                const smDatasets: any[] = [];
                const rnDatasets: any[] = [];
                comparisons.forEach((comp, idx) => {
                  const valuesOnUnion = mapToUnion(comp);
                  const hue = (idx * 360) / Math.max(1, comparisons.length);
                  if (comp.parameter === 'SM') {
                    smDatasets.push({
                      type: 'line' as const,
                      label: `${comp.state} - SM`,
                      data: valuesOnUnion,
                      borderColor: `hsl(${hue}, 70%, 40%)`,
                      backgroundColor: `hsla(${hue}, 70%, 40%, 0.12)`,
                      tension: 0.25,
                      pointRadius: 0,
                      yAxisID: 'ySm',
                      fill: false,
                    });
                  } else {
                    rnDatasets.push({
                      type: 'bar' as const,
                      label: `${comp.state} - Rainfall`,
                      data: valuesOnUnion,
                      borderColor: `hsl(${hue}, 70%, 45%)`,
                      backgroundColor: `hsla(${hue}, 70%, 45%, 0.35)`,
                      yAxisID: 'yRain',
                    });
                  }
                });

                return (
                  <ReactChart
                    type={'bar'}
                    data={{ labels, datasets: [...rnDatasets, ...smDatasets] }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      animation: { duration: 700, easing: 'easeOutQuart' },
                      plugins: {
                        legend: { position: 'top' as const },
                        tooltip: {
                          mode: 'index',
                          intersect: false,
                          backgroundColor: 'rgba(255,255,255,0.95)',
                          titleColor: '#941C30',
                          bodyColor: '#1E0C0D',
                          borderColor: '#941C30',
                          borderWidth: 1,
                          padding: 8,
                        },
                      },
                      interaction: { mode: 'index', intersect: false },
                      scales: {
                        x: { grid: { color: 'rgba(0,0,0,0.06)' }, ticks: { maxTicksLimit: 8 } },
                        ySm: {
                          type: 'linear' as const,
                          position: 'left' as const,
                          grid: { color: 'rgba(0,0,0,0.06)' },
                          title: { display: true, text: 'Soil Moisture (m3/m3)' },
                        },
                        yRain: {
                          type: 'linear' as const,
                          position: 'right' as const,
                          grid: { drawOnChartArea: false },
                          title: { display: true, text: 'Rainfall (mm/day)' },
                          beginAtZero: true,
                        },
                      },
                    }}
                    height={isFullscreen ? 500 : 300}
                  />
                );
              })()}
            </div>

            <div className="comparison-stats">
              {comparisons.map((comp, index) => {
                const stats = calculateStats(comp.data.values);
                return (
                  <div key={index} className="stat-card">
                    <div className="stat-header">
                      <h4>{comp.state} - {comp.parameter}</h4>
                      <button onClick={() => removeComparison(index)} className="remove-btn">×</button>
                    </div>
                    <div className="stat-values">
                      <div>Average: {stats.average.toFixed(2)}</div>
                      <div>Maximum: {stats.maximum.toFixed(2)}</div>
                      <div>Minimum: {stats.minimum.toFixed(2)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="no-comparisons-message">
            <p>Add states and parameters to compare using the controls above</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComparisonPanel; 